import React, { useMemo } from 'react';
import {
	Box,
	Text,
	TableContainer,
	Table,
	Thead,
	Tr,
	Th,
	Tbody,
	Td,
	Button,
} from '@chakra-ui/react';
import Locale from '../../../utils/translations';

type TemplateResult = {
	templateId: string;
	name?: string;
	wer?: number;
	_id?: string;
};

type RunRow = {
	date?: string | Date;
	testId?: string;
	testName?: string;
	sampleId?: string;
	templateResults?: TemplateResult[];
};

interface TestRunsTableProps {
	historyOfRuns: RunRow[];
	onCalculateTranslationOpen: (sampleId: string, template: TemplateResult) => void;
}

const TestRunsTable: React.FC<TestRunsTableProps> = ({
	historyOfRuns,
	onCalculateTranslationOpen,
}) => {
	const sortedRuns = useMemo(() => {
		if (!Array.isArray(historyOfRuns)) {
			return [];
		}
		return [...historyOfRuns].sort(
			(a, b) => new Date(b.date ?? 0).getTime() - new Date(a.date ?? 0).getTime(),
		);
	}, [historyOfRuns]);

	if (!sortedRuns.length) {
		return null;
	}

	return (
		<Box mt={2}>
			<Text fontWeight="bold" fontSize="md" mb={2}>
				Test runs:
			</Text>

			{sortedRuns.map((run, runIdx) => (
				<Box key={run.testId ?? runIdx} mb={6}>
					<Text fontWeight="semibold" fontSize="lg" mb={2}>
						{run.testName} – {run.date ? new Date(run.date).toLocaleString() : ''}
					</Text>

					<TableContainer borderRadius="md" overflow="auto" boxShadow="sm">
						<Table
							size="sm"
							variant="simple"
							sx={{
								tableLayout: 'fixed', // ensures equal-width columns
								width: '100%',
							}}
						>
							<Thead bg="#adedf4">
								<Tr>
									<Th
										color="black"
										fontWeight="bold"
										border="1px solid"
										borderColor="gray.300"
										w="5%"
										textAlign="center"
									>
										#
									</Th>
									<Th
										color="black"
										fontWeight="bold"
										border="1px solid"
										borderColor="gray.300"
										w="35%"
										textAlign="center"
									>
										Template
									</Th>
									<Th
										color="black"
										fontWeight="bold"
										border="1px solid"
										borderColor="gray.300"
										w="20%"
										textAlign="center"
									>
										WER
									</Th>
									<Th
										color="black"
										fontWeight="bold"
										border="1px solid"
										borderColor="gray.300"
										w="20%"
										textAlign="center"
										position="sticky"
										right={0}
										bg="#adedf4"
										zIndex={1}
									>
										Actions
									</Th>
								</Tr>
							</Thead>

							<Tbody>
								{(run.templateResults ?? []).map((result, idx) => (
									<Tr
										key={result._id ?? `${result.templateId}-${idx}`}
										bg={idx % 2 === 0 ? 'white' : 'gray.50'}
									>
										<Td
											border="1px solid"
											borderColor="gray.300"
											textAlign="center"
										>
											{idx + 1}
										</Td>
										<Td
											border="1px solid"
											borderColor="gray.300"
											textAlign="center"
											whiteSpace="nowrap"
											overflow="hidden"
											textOverflow="ellipsis"
										>
											{result.name || result.templateId}
										</Td>
										<Td
											border="1px solid"
											borderColor="gray.300"
											textAlign="center"
										>
											<b>{result.wer ?? '-'}</b>
										</Td>
										<Td
											border="1px solid"
											borderColor="gray.300"
											right={0}
											bg={idx % 2 === 0 ? 'white' : 'gray.50'}
											textAlign="center"
										>
											<Button
												size="sm"
												variant="bePrimary"
												margin={'auto'}
												onClick={() =>
													onCalculateTranslationOpen(
														run.sampleId || '',
														result,
													)
												}
											>
												{Locale.get('Calculate Translation')}
											</Button>
										</Td>
									</Tr>
								))}
							</Tbody>
						</Table>
					</TableContainer>
				</Box>
			))}
		</Box>
	);
};

export default React.memo(TestRunsTable);
